import { useState, useRef, useEffect } from "react";
import { useBookingUpdate } from '../../hooks/useBookingUpdate';
import { useNavigate } from "react-router-dom";

import classes from "./BookingContainer.module.css";
import Modal from "../UI/Modal";
import ChatLink from "../ChatLink";
import SaveNotification from "../UI/SaveNotification/SaveNotification";
import { formatBerlinDateTimeRange, isCompletedBookingStatus } from "../../util/bookingDateTime";

export const BookingContainer = ({ booking, date, onBookingUpdate }) => {
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const [openModal, setOpenModal] = useState(false);
  const { updateBookingStatus, isLoading: isBookingUpdateLoading } = useBookingUpdate();
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  // Helper functions for status display (from BookingDetailsCard.jsx)
  const calculateHoursRemaining = (createdAt) => {
    if (!createdAt) return null;
    const created = new Date(createdAt);
    const now = new Date();
    const hoursPassed = (now - created) / (1000 * 60 * 60);
    const hoursRemaining = Math.max(0, Math.ceil(24 - hoursPassed));
    return hoursRemaining;
  };

  const isBookingExpired = (createdAt) => {
    if (!createdAt) return false;
    const created = new Date(createdAt);
    const now = new Date();
    const hoursPassed = (now - created) / (1000 * 60 * 60);
    return hoursPassed > 24;
  };

  const getStatusDisplay = () => {
    const status = booking?.status;
    const guestName = booking?.userData?.firstName || booking?.user?.split(' ')[0] || '';
    const createdAt = booking?.createdAt;
    const hoursRemaining = calculateHoursRemaining(createdAt);
    const expired = isBookingExpired(createdAt);
    const completed = isCompletedBookingStatus(status);

    if (completed) {
      return {
        message: '',
        statusText: 'Abgeschlossen',
        color: '#A1A7AE'
      };
    }

    if (status === "INQUIRY" || (status === "PENDING" && !expired && hoursRemaining === null)) {
      return {
        message: `Erkundigung von ${guestName}`,
        statusText: 'Erkundigung',
        color: '#2788EE'
      };
    }

    if (status === "PENDING" && !expired) {
      return {
        message: `${guestName} hat bei dir angefragt`,
        statusText: hoursRemaining !== null ? `Angefragt: Läuft in ${hoursRemaining} ${hoursRemaining === 1 ? 'Stunde' : 'Stunden'} ab` : 'Angefragt',
        color: '#F7871E'
      };
    }

    if (status === "CONFIRMED") {
      return {
        message: `Du hast die Buchung bestätigt`,
        statusText: 'Buchung bestätigt',
        color: '#20BD13'
      };
    }

    // Default fallback
    return {
      message: '',
      statusText: 'Unbekannt',
      color: '#A1A7AE'
    };
  };

  const generateICSFile = () => {
    try {
        if (!booking?.startDateTime || !booking?.endDateTime) {
            console.error('Missing date or time information for calendar export');
            return;
        }
        const eventDateTime = new Date(booking.startDateTime);
        const endDateTime = new Date(booking.endDateTime);

        // Validate that dates are valid
        if (isNaN(eventDateTime.getTime()) || isNaN(endDateTime.getTime())) {
            console.error('Invalid date objects created');
            return;
        }

        // Format dates for ICS file
        const formatDateForICS = (date) => {
            return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        };

        const icsContent = `BEGIN:VCALENDAR
          VERSION:2.0
          BEGIN:VEVENT
          DTSTART:${formatDateForICS(eventDateTime)}
          DTEND:${formatDateForICS(endDateTime)}
          SUMMARY:Termin bei ${booking?.studioName}
          LOCATION:${booking?.studioLocation || ''}
          END:VEVENT
          END:VCALENDAR`;

        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `termin_${(booking?.studioName || 'studio').replace(/\s+/g, '_')}.ics`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
          console.error('Error generating ICS file:', error);
      }
  };
  
  // Helper function to format booking details for first message
  const formatBookingDetailsMessage = (booking, date) => {
    if (!booking || !date) return "";
    
    const statusDisplay = getStatusDisplay();
    const dateTime = formatBerlinDateTimeRange(booking.startDateTime, booking.endDateTime, {
      dateOptions: {
        weekday: 'long',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      },
      timeSuffix: '',
    });
    
    return `Hallo! Ich schreibe bezüglich der Buchung:\n\n📅 ${dateTime?.startDate || date}\n⏰ ${dateTime?.startTime || booking.startTime} - ${dateTime?.endTime || booking.endTime}\n🏢 ${booking.studioName}\n💰 ${booking.price}\n\nProjekt: ${booking.projectName}\nStatus: ${statusDisplay.statusText}`;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target)
      ) {
        setOpenModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openModal]);

  
  const handleConfirmBooking = async () => {
      try {
          await updateBookingStatus(booking.id, "CONFIRMED");
          setNotificationMessage('Buchung bestätigt');
          setShowNotification(true);
          setOpenModal(false);
          
          // Trigger calendar data refresh
          if (onBookingUpdate) {
            onBookingUpdate();
          }
      } catch (error) {
          console.error('Failed to confirm booking:', error);
          setNotificationMessage('Fehler bei der Bestätigung der Buchung');
          setShowNotification(true);
      }
  };

  const handleDeclineBooking = async () => {
      try {
          await updateBookingStatus(booking.id, "DECLINED");
          setNotificationMessage('Buchung abgelehnt');
          setShowNotification(true);
          setOpenModal(false);
          
          // Trigger calendar data refresh
          if (onBookingUpdate) {
            onBookingUpdate();
          }
      } catch (error) {
          console.error('Failed to decline booking:', error);
          setNotificationMessage('Fehler beim Ablehnen der Buchung');
          setShowNotification(true);
      }
  };

  const handleNavigateToBooking = () => {
    navigate(`/creator/details/${booking.id}`, {
      state: { 
        userId: booking.userData?.id,
        userName: booking.userData ? `${booking.userData.firstName} ${booking.userData.lastName}` : "",
        userImage: booking.userData?.processedProfilePicture
      }
    });
  };

  const handleBackdropClick = () => {
      setOpenModal(false);
  };

  const openBookingModal = () => {
    setOpenModal(true);
  };

  const calculatePeriodOfBooking = (booking) => {
    if (booking?.startDateTime && booking?.endDateTime) {
      const startTime = new Date(booking.startDateTime);
      const endTime = new Date(booking.endDateTime);
      if (!Number.isNaN(startTime.getTime()) && !Number.isNaN(endTime.getTime())) {
        const timeDifference = endTime - startTime;
        return Math.max(0, Math.floor(timeDifference / (1000 * 60 * 60)));
      }
    }

    if (!booking?.date || !booking?.startTime || !booking?.endTime) return 0;

    try {
      const [day, month, year] = booking.date.split('.');
      if (!day || !month || !year) return 0;
      const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

      const startTime = new Date(`${formattedDate}T${booking.startTime}`);
      const endTime = new Date(`${formattedDate}T${booking.endTime}`);
      if (Number.isNaN(startTime.getTime()) || Number.isNaN(endTime.getTime())) return 0;

      const timeDifference = endTime - startTime;
      return Math.max(0, Math.floor(timeDifference / (1000 * 60 * 60)));
    } catch {
      return 0;
    }
  };

  const statusDisplay = getStatusDisplay();

  return (
    <div className={classes.bookingContainer} onClick={openBookingModal}>
        <div className={classes.bookingItem}>
              <div className={classes.studioImageContainer}>
                {booking.studioImage && (
                  <img 
                    src={booking.studioImage} 
                    alt="Studio" 
                    className={classes.studioImage}
                  />
                )}
              </div>
              <div className={classes.bookingDetails}>
                <div className={classes.bookingTime}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="14" viewBox="0 0 13 14" fill="none">
                    <path d="M6.25056 3.39897V7.71868M6.10022 7.43824L9.30022 9.15957M12.5 7C12.5 10.3137 9.81371 13 6.5 13C3.18629 13 0.5 10.3137 0.5 7C0.5 3.68629 3.18629 1 6.5 1C9.81371 1 12.5 3.68629 12.5 7Z" stroke="#1F1F1F" strokeMiterlimit="10"/>
                  </svg>
                  <span className="regularSmall">{booking.startTime} - {booking.endTime} Uhr</span>
                </div>
                <div className={classes.bookingStatus}>
                  <span style={{color: statusDisplay.color}} className="regularXxs">{statusDisplay.statusText}</span>
                  •
                  <span className="regularXxs">{booking.user}</span>
                </div>
                <span className="regularXxs">{booking.studioName}</span>
                <span className="regularXxs">{booking.price}</span>
              </div>  
        </div>
        <button className={classes.bookingButton}>
          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="19" viewBox="0 0 11 19" fill="none">
            <path fillRule="evenodd" clipRule="evenodd" d="M10.998 9.87997L11.0054 9.88808L10.8538 10.0262L2.0082 18.9914L0.798069 17.7974L8.65601 9.83318L0.75 1.1525L2.00685 0.0078125L10.8413 9.70789L11.0067 9.87113L10.998 9.87997Z" fill="#1F1F1F"/>
          </svg>
        </button>
        {openModal && (
          <Modal open={openModal} onBackdropClick={handleBackdropClick}>
            <div ref={modalRef} className={classes.modalContainer}>
              <header className={classes.modalHeader}>
                <h2 className="mediumLarge">{booking.projectName}</h2>
                <button
                  className={classes.closeButton}   
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenModal(false);
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                  </svg>
                </button>
              </header>
              <div className={classes.modalContent}>
                <div className={classes.studioInfos}>
                  <div className={classes.studioModalImageContainer}>
                    <img src={booking.studioImage} alt="Studio" className={classes.studioModalImage} />
                  </div>
                  <div className={classes.studioModalInfos}>
                    <span className="mediumLarge">{booking.studioName}</span>
                    <span style={{color: "var(--dark-grey-3)"}} className="regularXxs">{booking.studioLocation}</span>
                  </div>
                </div>
                <div className={classes.modalStatus}>
                  <h3 className="semiboldMiddle">Status</h3>
                  {statusDisplay.message && (
                    <p style={{marginBottom: '10px', color: 'var(--dark-grey-3)'}} className="regularLarge">
                      {statusDisplay.message}
                    </p>
                  )}
                  <span style={{color: statusDisplay.color}} className="semiboldLarge">
                    {statusDisplay.statusText}
                  </span>
                </div>
                <div className={classes.modalBookingDetails}>
                  <h2 className="titleSemibold3">Buchungsdetails</h2>
                  <div className={classes.bookingDetails}>
                    <h4 className="semiboldMiddle">Datum und Zeit</h4>
                    <span className="regularLarge">{date}</span>
                    <span className="regularLarge">{booking.startTime} - {booking.endTime} Uhr</span>
                    <p className="regularXxs">Dauer: {calculatePeriodOfBooking(booking)} Stunden</p>
                  </div>
                  <button onClick={generateICSFile} className={classes.exportButton}>In Kalender exportieren</button>
                  <div className={classes.thinLine}></div>
                </div>
                <div className={classes.participantsContainer}>
                  <h2 className="semiboldMiddle">Teilnehmer</h2>
                  <span className="regularLarge">1-5 Personen</span>
                </div>
              </div>
              <footer className={classes.modalFooter}>
                <div className={classes.buttonContainer}>
                  {booking.status === "PENDING" ? (
                      <>
                        <button 
                          onClick={handleDeclineBooking}
                          className={`${classes.rejectButton} ${classes.button} mediumLarge`}
                          disabled={isBookingUpdateLoading}
                          style={{
                            opacity: isBookingUpdateLoading ? 0.6 : 1,
                            cursor: isBookingUpdateLoading ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {isBookingUpdateLoading ? 'Wird abgelehnt...' : 'Ablehnen'}
                        </button>
                        <button 
                          onClick={handleConfirmBooking} 
                          className={`${classes.acceptButton} ${classes.button} mediumLarge`}
                          disabled={isBookingUpdateLoading}
                          style={{
                            opacity: isBookingUpdateLoading ? 0.6 : 1,
                            cursor: isBookingUpdateLoading ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {isBookingUpdateLoading ? 'Wird bestätigt...' : 'Akzeptieren'}
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={handleNavigateToBooking} className={`${classes.rejectButton} ${classes.button} ${classes.noPhoneButton} mediumLarge`}>
                          Zur Buchungsansicht
                        </button>
                        <button onClick={handleNavigateToBooking} className={`${classes.rejectButton} ${classes.button} ${classes.phoneButton} mediumLarge`}>
                          Zur Buchung
                        </button>
                        <ChatLink 
                          userId={booking?.userData?.id}
                          studioId={booking?.studioData?.id}
                          studioName={booking?.studioName}
                          studioImage={booking?.studioImage}
                          className={`${classes.acceptButton} ${classes.button} mediumLarge`}
                          firstMessage={formatBookingDetailsMessage(booking, date)}
                          chatRoomId={booking?.chatRoomID}
                          bookingId={booking?.id}
                        >
                          Nachricht
                        </ChatLink>
                      </>
                    )
                  }
                </div>
                {/* {booking.status !== "CONFIRMED" && (
                <div className={classes.statusContainer}>
                  <span style={{color: "var(--dark-grey-4)"}} className="semiboldXs">Anfrage läuft ab in</span>
                  <span style={{color: "var(--stu-rot)"}} className="semiboldXs">3 Stunden</span>
                  </div>
                )} */}
              </footer>
            </div>
          </Modal>
        )}
        <SaveNotification 
          show={showNotification} 
          onHide={() => setShowNotification(false)}
          message={notificationMessage}
        />
    </div>
  );
};

export default BookingContainer;
